import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import axiosInstance from "../utlis/axiosInstance";
import DeviceSearchSelect, { DeviceOption } from "./DeviceSearchSelect";

interface GeofenceConnectionsFormProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

interface Device {
  id: string;
  name: string;
  unique_id: string;
  status: string;
  model: string;
  contact: string;
  category: string;
}

const GeofenceConnectionsForm: React.FC<GeofenceConnectionsFormProps> = ({
  id,
  open,
  onClose,
}) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceOption | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open && id) {
      fetchDevices();
    }
  }, [open, id]);

  const fetchDevices = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/gpstracker/configurationhandler/?limit=1000&offset=0&geofence_id=${id}`,
      );
      if (
        response.data &&
        response.data.responseData &&
        response.data.responseData.devices
      ) {
        setDevices(response.data.responseData.devices);
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
      setError("Failed to fetch connected devices");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = async () => {
    if (!selectedDevice || !id) return;

    try {
      setLoading(true);
      const requestBody = {
        user_id: null,
        device_id: parseInt(selectedDevice.id),
        group_id: null,
        geofence_id: parseInt(id),
        notification_id: null,
        calendar_id: null,
        attribute_id: null,
        driver_id: null,
        managed_user_id: null,
        command_id: null,
      };

      await axiosInstance.post(
        "/gpstracker/configurationhandler/",
        requestBody,
      );
      setSuccess("Device connected successfully");
      setSelectedDevice(null);
      fetchDevices();
    } catch (error) {
      console.error("Error connecting device:", error);
      setError("Failed to connect device");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDevice = async (id: string) => {
    if (!id) return;

    try {
      setLoading(true);
      await axiosInstance.delete(`/gpstracker/configurationhandler/${id}`);
      setSuccess("Device disconnected successfully");
      fetchDevices();
    } catch (error) {
      console.error("Error removing device:", error);
      setError("Failed to disconnect device");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Geofence Device Connections
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Connected Devices
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  minHeight: 100,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {devices.length === 0 ? (
                  <Typography
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No devices connected
                  </Typography>
                ) : (
                  devices.map((device: any) => (
                    <Chip
                      key={device.id}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600 }}
                          >
                            {device.name}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            ID: {device.unique_id}
                          </Typography>
                        </Box>
                      }
                      onDelete={() =>
                        handleRemoveDevice(device.configuration_id)
                      }
                      sx={{
                        height: "auto",
                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal",
                          py: 0.5,
                        },
                      }}
                    />
                  ))
                )}
              </Paper>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <DeviceSearchSelect
                label="Search & Add Device"
                value={selectedDevice}
                onChange={(val) =>
                  setSelectedDevice(val as DeviceOption | null)
                }
                disabled={loading}
              />
              <Button
                variant="contained"
                onClick={handleAddDevice}
                disabled={!selectedDevice || loading}
                sx={{
                  minWidth: 100,
                  background:
                    "linear-gradient(135deg, #0e67e9 0%, #1976d2 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0e67e9 0%, #1565c0 100%)",
                  },
                }}
              >
                Add
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{ borderTop: "1px solid", borderColor: "divider", px: 3, py: 2 }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#0e67e9",
            color: "#0e67e9",
            "&:hover": {
              borderColor: "#0e67e9",
              bgcolor: "rgba(14, 103, 233, 0.1)",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeofenceConnectionsForm;
